import { ApplicationStatusEmailRequestDto } from '@/models/Admin/Emails/EmailRequestDtos';
import { ApplicationTypes } from '@/models/ApplicationType';
import { ProgramNames } from '@/models/Program';
import { ApplicationStatus } from '@/utils/constants';
import { isEmail } from '@/utils/emailUtil';
import React, { useEffect, useState } from 'react';
import ApplicationStatusForm from './ApplicationStatusForm';
import { ApplicationStatusSubmitState } from './types';

type Props = {
  defaultLocale: string;
  localeOptions: ReadonlyArray<{ value: string; label: string }>;
  onStateChange: React.Dispatch<React.SetStateAction<ApplicationStatusSubmitState>>;
  resetSignal: number;
};

const programOptions = Object.values(ProgramNames).filter(
  (value): value is ProgramNames => typeof value === 'number'
);

const applicationTypeOptions = Object.values(ApplicationTypes).filter(
  (value): value is ApplicationTypes => typeof value === 'number'
);

const applicationStatusOptions = Object.values(ApplicationStatus).filter(
  (value): value is ApplicationStatus => typeof value === 'number'
);

const createInitialForm = (defaultLocale: string): ApplicationStatusEmailRequestDto => ({
  Email: '',
  ApplicationId: 0,
  Program: ProgramNames.TreeCityUSA,
  PartnerName: '',
  RecognitionYear: new Date().getFullYear(),
  LocaleCode: defaultLocale,
  HasStateCoordinator: false,
  HasRegionalCoordinator: false,
  HasNationalProgrammeLead: false,
  NewStatus: ApplicationStatus.Submitted,
  RevisionStatus: null,
  IsResubmitted: false,
  ApplicationType: ApplicationTypes.Standard,
});

const createEmptySubmitState = (defaultLocale: string): ApplicationStatusSubmitState => ({
  request: createInitialForm(defaultLocale),
  isValid: false,
});

const ApplicationStatusSection: React.FC<Props> = ({
  defaultLocale,
  localeOptions,
  onStateChange,
  resetSignal,
}) => {
  const [form, setForm] = useState<ApplicationStatusEmailRequestDto>(
    createInitialForm(defaultLocale)
  );

  const isValid =
    isEmail(form.Email) &&
    form.ApplicationId > 0 &&
    form.PartnerName.trim() !== '' &&
    form.RecognitionYear > 0 &&
    (form.NewStatus !== ApplicationStatus.InRevision || form.RevisionStatus != null);

  useEffect(() => {
    onStateChange({
      request: form,
      isValid,
    });
  }, [form, isValid, onStateChange]);

  useEffect(() => {
    setForm(createInitialForm(defaultLocale));
    onStateChange(createEmptySubmitState(defaultLocale));
  }, [defaultLocale, onStateChange, resetSignal]);

  return (
    <ApplicationStatusForm
      form={form}
      setForm={setForm}
      programOptions={programOptions}
      applicationStatusOptions={applicationStatusOptions}
      applicationTypeOptions={applicationTypeOptions}
      localeOptions={localeOptions}
      defaultLocale={defaultLocale}
    />
  );
};

export default ApplicationStatusSection;
